import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  setDoc,
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

// Posts collection
export const postsCollection = collection(db, 'posts');
export const commentsCollection = collection(db, 'comments');
export const likesCollection = collection(db, 'likes');

// Posts CRUD operations
export const createPost = async (postData) => {
  try {
    const docRef = await addDoc(postsCollection, {
      ...postData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      views: 0,
      likes: 0,
      comments: 0
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const updatePost = async (postId, postData) => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      ...postData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw error;
  }
};

export const deletePost = async (postId) => {
  try {
    const postRef = doc(db, 'posts', postId);
    await deleteDoc(postRef);
  } catch (error) {
    throw error;
  }
};

export const getPost = async (slug) => {
  try {
    // Search for post by slug
    const q = query(
      postsCollection,
      where('slug', '==', slug),
      where('published', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } else {
      throw new Error('Post not found');
    }
  } catch (error) {
    throw error;
  }
};

export const getPostById = async (postId) => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postSnap = await getDoc(postRef);
    
    if (postSnap.exists()) {
      return { id: postSnap.id, ...postSnap.data() };
    } else {
      throw new Error('Post not found');
    }
  } catch (error) {
    throw error;
  }
};

export const getPosts = async (limitCount = 10, lastDoc = null) => {
  try {
    let q = query(
      postsCollection,
      where('published', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    let lastVisible = null;
    
    querySnapshot.forEach((doc) => {
      posts.push({ id: doc.id, ...doc.data() });
      lastVisible = doc;
    });
    
    return { posts, lastVisible };
  } catch (error) {
    throw error;
  }
};

export const getPostsByCategory = async (category, limitCount = 10) => {
  try {
    const q = query(
      postsCollection,
      where('published', '==', true),
      where('category', '==', category),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    
    querySnapshot.forEach((doc) => {
      posts.push({ id: doc.id, ...doc.data() });
    });
    
    return posts;
  } catch (error) {
    throw error;
  }
};

export const searchPosts = async (searchTerm) => {
  try {
    const q = query(
      postsCollection,
      where('published', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const posts = [];
    
    querySnapshot.forEach((doc) => {
      const post = { id: doc.id, ...doc.data() };
      const searchableText = `${post.title} ${post.content} ${post.tags?.join(' ')}`.toLowerCase();
      
      if (searchableText.includes(searchTerm.toLowerCase())) {
        posts.push(post);
      }
    });
    
    return posts;
  } catch (error) {
    throw error;
  }
};

// Comments operations
export const addComment = async (postId, commentData) => {
  try {
    const commentRef = await addDoc(commentsCollection, {
      ...commentData,
      postId,
      createdAt: serverTimestamp(),
      approved: false
    });
    
    // Update post comment count
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, {
      comments: increment(1)
    });
    
    return commentRef.id;
  } catch (error) {
    throw error;
  }
};

// export const getComments = async (postId) => {
//   try {
//     const q = query(
//       commentsCollection,
//       where('postId', '==', postId),
//       where('approved', '==', true),
//       orderBy('createdAt', 'desc')
//     );
    
//     const querySnapshot = await getDocs(q);
//     const comments = [];
    
//     querySnapshot.forEach((doc) => {
//       comments.push({ id: doc.id, ...doc.data() });
//     });
    
//     return comments;
//   } catch (error) {
//     throw error;
//   }
// };


export const getComments = async (postId) => {
  try {
    const q = query(
      commentsCollection,
      where('postId', '==', postId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const comments = [];
    
    querySnapshot.forEach((doc) => {
      comments.push({ id: doc.id, ...doc.data() });
    });
    
    return comments;
  } catch (error) {
    throw error;
  }
};

export const approveComment = async (commentId) => {
  try {
    const commentRef = doc(db, 'comments', commentId);
    await updateDoc(commentRef, {
      approved: true
    });
  } catch (error) {
    throw error;
  }
};

export const deleteComment = async (commentId) => {
  try {
    const commentRef = doc(db, 'comments', commentId);
    await deleteDoc(commentRef);
  } catch (error) {
    throw error;
  }
};

export const getAllComments = async () => {
  try {
    const q = query(commentsCollection, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const comments = [];
    querySnapshot.forEach((doc) => {
      comments.push({ id: doc.id, ...doc.data() });
    });
    return comments;
  } catch (error) {
    throw error;
  }
};

// Likes operations
export const toggleLike = async (postId, userId) => {
  try {
    const likeRef = doc(db, 'likes', `${postId}_${userId}`);
    const likeSnap = await getDoc(likeRef);
    
    if (likeSnap.exists()) {
      // Unlike
      await deleteDoc(likeRef);
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        likes: increment(-1)
      });
      return false;
    } else {
      // Like
      await setDoc(likeRef, {
        postId,
        userId,
        createdAt: serverTimestamp()
      });
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        likes: increment(1)
      });
      return true;
    }
  } catch (error) {
    throw error;
  }
};

export const checkUserLiked = async (postId, userId) => {
  try {
    const likeRef = doc(db, 'likes', `${postId}_${userId}`);
    const likeSnap = await getDoc(likeRef);
    return likeSnap.exists();
  } catch (error) {
    return false;
  }
};

// Analytics
export const getAnalytics = async () => {
  try {
    const postsQuery = query(postsCollection, where('published', '==', true));
    const commentsQuery = query(commentsCollection, where('approved', '==', true));
    
    const [postsSnapshot, commentsSnapshot] = await Promise.all([
      getDocs(postsQuery),
      getDocs(commentsQuery)
    ]);
    
    let totalViews = 0;
    let totalLikes = 0;
    
    postsSnapshot.forEach((doc) => {
      const data = doc.data();
      totalViews += data.views || 0;
      totalLikes += data.likes || 0;
    });
    
    return {
      totalPosts: postsSnapshot.size,
      totalViews,
      totalLikes,
      totalComments: commentsSnapshot.size
    };
  } catch (error) {
    throw error;
  }
};

export const incrementPostView = async (postId) => {
  try {
    const postRef = doc(db, 'posts', postId);
    await updateDoc(postRef, { views: increment(1) });
  } catch (error) {
    throw error;
  }
};
